import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/10k30", label: "10K30" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
];

export default function MarketingLayout({ children }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/90 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="wordmark text-2xl font-bold tracking-wide" data-testid="brand-link" onClick={() => setOpen(false)}>
            BA<span className="text-primary">30</span>
          </Link>
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to}
                className={({ isActive }) => `text-xs uppercase tracking-[0.18em] font-semibold transition-colors ${isActive ? "text-primary" : "text-foreground/75 hover:text-foreground"}`}
                data-testid={`nav-${n.label.toLowerCase()}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <Link to="/app" className="btn-primary btn-sm" data-testid="header-portal-btn">Open Portal</Link>
            ) : (
              <>
                <Link to="/login" className="hidden sm:inline-flex btn-ghost text-xs uppercase tracking-wider font-semibold" data-testid="header-login-btn">Log in</Link>
                <Link to="/join" className="btn-primary btn-sm" data-testid="header-join-btn" data-event="click_join_ba30">Join BA30</Link>
              </>
            )}
            <button onClick={() => setOpen((o) => !o)} className="lg:hidden p-2 -mr-2 rounded-md hover:bg-muted" aria-label="Toggle menu" data-testid="mobile-menu-btn">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
        {open && (
          <nav className="lg:hidden border-t border-border bg-background" data-testid="mobile-nav">
            <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
                  className={({ isActive }) => `px-3 py-3 rounded-md font-head uppercase tracking-wider text-sm ${isActive ? "bg-primary/10 text-primary" : "text-foreground/85 hover:bg-muted"}`}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}>{n.label}</NavLink>
              ))}
              {!user && (
                <NavLink to="/login" onClick={() => setOpen(false)} className="px-3 py-3 rounded-md font-head uppercase tracking-wider text-sm hover:bg-muted">Log in</NavLink>
              )}
            </div>
          </nav>
        )}
      </header>
      <main>{children}</main>
      <footer className="mt-24 bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid gap-10 md:grid-cols-4">
          <div>
            <div className="wordmark text-3xl font-bold">BUILT AFTER <span className="text-primary">30</span></div>
            <p className="text-sm text-background/70 mt-4 max-w-xs">Walk more. Lift heavy. Eat real food. Live your life.</p>
          </div>
          <div>
            <div className="label-eyebrow !text-background/60 mb-3">Program</div>
            <ul className="space-y-2 text-sm">
              <li><Link className="hover:text-primary" to="/10k30">10K30 — The 12-Week Longevity Foundation</Link></li>
              <li><Link className="hover:text-primary" to="/blog">Blog</Link></li>
            </ul>
          </div>
          <div>
            <div className="label-eyebrow !text-background/60 mb-3">Company</div>
            <ul className="space-y-2 text-sm">
              <li><Link className="hover:text-primary" to="/about">About</Link></li>
            </ul>
          </div>
          <div>
            <div className="label-eyebrow !text-background/60 mb-3">Connect</div>
            <ul className="space-y-2 text-sm">
              <li><Link className="hover:text-primary" to="/privacy">Privacy</Link></li>
              <li><Link className="hover:text-primary" to="/terms">Terms</Link></li>
              <li><a className="hover:text-primary" href="mailto:hello@ba30.com">Contact</a></li>
            </ul>
          </div>
        </div>
        <div className="text-xs text-background/50 text-center pb-8 uppercase tracking-widest">© {new Date().getFullYear()} Built After 30.</div>
      </footer>
    </div>
  );
}
